import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../constants/providers/AuthProvider';
import { NotificationProvider } from '../../context/NotificationContext';
import { AuthenticatedLayoutContent } from './AuthenticatedLayout';

interface AuthenticatedLayoutProps {
  children?: React.ReactNode;
  /**
   * Whether to wrap content in PageLayout
   * @default true
   */
  usePageLayout?: boolean;
  /**
   * PageLayout maxWidth prop (only used if usePageLayout is true)
   * @default '7xl'
   */
  pageMaxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
  /**
   * PageLayout padding prop (only used if usePageLayout is true)
   * @default 'md'
   */
  pagePadding?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * AuthenticatedLayout component for the refactored architecture
 *
 * This component validates authentication and provides the layout structure.
 * Sidebar providers are now handled at the app level by AuthenticatedLayoutWrapper.
 * 
 * Supports two modes:
 * - Nested routing: Uses <Outlet /> when no children provided (layout stays mounted)
 * - Direct children: Uses children prop when provided (legacy support)
 */
export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = (props) => {
  const { session } = useAuth();

  // Safety check - this shouldn't happen due to AuthenticatedLayoutWrapper,
  // but provides fallback redirect if user somehow reaches here without auth
  if (!session) {
    return <Navigate to="/signin" replace />;
  }

  // Use Outlet for nested routing (prevents layout remount on navigation)
  // Fall back to children for backwards compatibility
  const content = props.children ?? <Outlet />;

  return (
    <NotificationProvider>
      <AuthenticatedLayoutContent {...props}>
        {content}
      </AuthenticatedLayoutContent>
    </NotificationProvider>
  );
};